import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Checkbox, Chip, Tooltip, Box, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { sa } from '../superAdminUi';

// Multi-select role picker for the Add User flow. Options come from the Identity role
// catalogue (code + name) enriched with a permission description; the operator may assign
// several roles to one user. A "Learn more" affordance reveals the permission blurbs — the
// same guidance the People Management app shows.

// The permission guide shown in the tooltip; built from the live options so it always
// matches exactly what is offered.
function RolePermissions({ options }) {
  return (
    <Box sx={{ p: 0.5, maxWidth: 340 }}>
      <Typography sx={{ fontFamily: sa.font.family, fontWeight: sa.font.bold, fontSize: 14, mb: 0.5 }}>
        Role Permissions
      </Typography>
      <Typography sx={{ fontFamily: sa.font.family, fontSize: 12.5, mb: 1 }}>
        Choose the role(s) you wish to assign. Each role gives different permissions:
      </Typography>
      {options.map((opt) => (
        <Box key={opt.code} sx={{ mb: 0.75 }}>
          <Typography sx={{ fontFamily: sa.font.family, fontWeight: sa.font.bold, fontSize: 12.5 }}>
            {opt.name}
          </Typography>
          {opt.description && (
            <Typography sx={{ fontFamily: sa.font.family, fontSize: 12.5, ml: 0.5 }}>
              {opt.description}
            </Typography>
          )}
        </Box>
      ))}
      <Typography sx={{ fontFamily: sa.font.family, fontSize: 12.5, fontStyle: 'italic', mt: 0.5 }}>
        You can assign multiple roles to the same user.
      </Typography>
    </Box>
  );
}
RolePermissions.propTypes = { options: PropTypes.array.isRequired };

const fieldSx = {
  fontFamily: sa.font.family,
  '& .MuiOutlinedInput-root': { borderRadius: sa.radius.sm, fontFamily: sa.font.family },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.stroke },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
};

function RoleMultiSelect({ options, value, onChange, error, helperText, loading, disabled }) {
  const selected = options.filter((opt) => value.includes(opt.code));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Typography
          component='label'
          sx={{ fontFamily: sa.font.family, fontSize: 13, fontWeight: sa.font.semibold, color: sa.color.text }}
        >
          Role
        </Typography>
        <Tooltip title={<RolePermissions options={options} />} arrow placement='right'>
          <Box
            component='span'
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: sa.color.teal, cursor: 'help' }}
          >
            <Typography sx={{ fontFamily: sa.font.family, fontSize: 12.5, textDecoration: 'underline' }}>
              Learn more
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
      </Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selected}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(opt, val) => opt.code === val.code}
        onChange={(_e, next) => onChange(next.map((opt) => opt.code))}
        renderOption={(props, opt, { selected: isSel }) => (
          <li {...props} key={opt.code}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
              checkedIcon={<CheckBoxIcon fontSize='small' />}
              checked={isSel}
              sx={{ mr: 1, color: sa.color.stroke, '&.Mui-checked': { color: sa.color.teal } }}
            />
            <Box>
              <Typography sx={{ fontFamily: sa.font.family, fontSize: 13.5, color: sa.color.text }}>
                {opt.name}
              </Typography>
              {opt.description && (
                <Typography sx={{ fontFamily: sa.font.family, fontSize: 12, color: sa.color.textGray }}>
                  {opt.description}
                </Typography>
              )}
            </Box>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((opt, index) => (
            <Chip
              {...getTagProps({ index })}
              key={opt.code}
              label={opt.name}
              size='small'
              sx={{
                fontFamily: sa.font.family,
                bgcolor: sa.color.schoolBg,
                color: sa.color.text,
                '& .MuiChip-deleteIcon': { color: sa.color.textGray },
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selected.length ? '' : 'Select role(s)'}
            error={error}
            helperText={helperText}
            sx={fieldSx}
          />
        )}
      />
    </Box>
  );
}

RoleMultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      id: PropTypes.number,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default RoleMultiSelect;
